import React from 'react';
import PropTypes from 'prop-types';
import { Tab } from 'react-bootstrap';
import numeral from 'numeral';

import { get, omit } from 'lodash';

import showsConfig from '../configs/shows-config';

import ShowAnticipation from './ShowAnticipation';
import ShowEngagement from './ShowEngagement';
import ShowInsights from './ShowInsights';
import BenchmarkBar from '../../components/BenchmarkBar';
import { FilterTypes } from '../../components/Filters';
import { buildMetricObj } from '../../utils/benchmark';

import './styles/ShowReports.css';
import ShowAffinity from "./ShowAffinity";

export const renderAnticipationTab = (
  {
    show,
    queryActions,
    filtersActions,
    filters,
    blocked,
    anticipationQueries,
    updateEpisodeOptionsLoader
  },
  currentTab,
  benchmarkData,
  updateBenchmarkData,
  benchmark,
  displayBenchmarkTooltip,
  setDisplayBenchmarkTooltip
) => {
  const benchmarkShowCount = get(
    benchmarkData,
    'showReportAnticipationBottom.showCount',
    ''
  );

  const metricA = buildMetricObj(
    benchmarkData,
    'showReportAnticipation',
    'followers',
    'Total Followers',
    'Measures total net users who are actively following a show.',
    '0,0'
  );
  const metricB = buildMetricObj(
    benchmarkData,
    'showReportAnticipation',
    'followerMovingAvg',
    'New Followers Last 7 Days',
    'Measures net new followers over the past 7 completed days.',
    '0,0'
  );
  const metricC = buildMetricObj(
    benchmarkData,
    'showReportAnticipation',
    'age',
    'Age Group Affinity',
    'Represents the highest follower indexing age bucket relative to general app population.'
  );
  const metricD = buildMetricObj(
    benchmarkData,
    'showReportAnticipation',
    'gender',
    'Gender Affinity',
    'Represents the highest follower indexing gender bucket relative to general app population.'
  );

  return (
    <Tab
      className="tvt-main__content-tab"
      eventKey={showsConfig.SHOW_ANTICIPATION_PAGE_ID}
      title="Anticipation"
    >
      {currentTab === showsConfig.SHOW_ANTICIPATION_PAGE_ID && (
        <>
          <BenchmarkBar
            loading={benchmarkData.loadingTop || benchmarkData.loadingBottom}
            appliedBenchmark={benchmark}
            applyBenchmarkFilters={updateBenchmarkData}
            filters={filters}
            titlesCount={numeral(benchmarkShowCount).format('0,0')}
            displayBenchmarkTooltip={displayBenchmarkTooltip}
            setDisplayBenchmarkTooltip={setDisplayBenchmarkTooltip}
            metricA={metricA}
            metricB={metricB}
            metricC={metricC}
            metricD={metricD}
          />
          <section className="tvt-main__content">
            <ShowAnticipation
              show={show}
              queryActions={queryActions}
              updateBenchmarkData={updateBenchmarkData}
              filtersActions={filtersActions}
              filters={filters}
              blocked={blocked}
              queries={anticipationQueries}
              updateEpisodeOptionsLoader={updateEpisodeOptionsLoader}
            />
          </section>
        </>
      )}
    </Tab>
  );
};

export const renderAudienceTab = (
  {
    show,
    viewershipQueries,
    queryActions,
    filtersActions,
    filters,
    blocked,
    updateEpisodeOptionsLoader
  },
  currentTab,
  benchmarkData,
  updateBenchmarkData,
  benchmark,
  displayBenchmarkTooltip,
  setDisplayBenchmarkTooltip
) => {
  const metricA = buildMetricObj(
    benchmarkData,
    'showReportViewership',
    'followerConversion',
    'Follower Conversion Rate',
    'Measures the percent of followers who have viewed at least one episode.',
    '0.00%'
  );
  const metricB = buildMetricObj(
    benchmarkData,
    'showReportViewership',
    'viewers',
    'Viewers',
    'Measures total unique users who have tracked at least one episode of that title.',
    '0,0'
  );
  const metricC = buildMetricObj(
    benchmarkData,
    'showReportViewership',
    'age',
    'Age Group Affinity',
    'Represents the highest viewer indexing age bucket relative to general app population.'
  );
  const metricD = buildMetricObj(
    benchmarkData,
    'showReportViewership',
    'gender',
    'Gender Affinity',
    'Represents highest viewer indexing gender bucket relative to general app population.'
  );

  return (
    <Tab
      className="tvt-main__content-tab"
      eventKey={showsConfig.SHOW_VIEWERSHIP_PAGE_ID}
      title="Audience"
    >
      {currentTab === showsConfig.SHOW_VIEWERSHIP_PAGE_ID && (
        <>
          <BenchmarkBar
            loading={benchmarkData.loadingTop || benchmarkData.loadingBottom}
            appliedBenchmark={benchmark}
            applyBenchmarkFilters={updateBenchmarkData}
            filters={filters}
            titlesCount={numeral(
              get(benchmarkData, 'showReportViewershipBottom.showCount', '')
            ).format('0,0')}
            metricA={metricA}
            metricB={metricB}
            metricC={metricC}
            metricD={metricD}
            shows={get(benchmarkData, ['tier', 'shows'], '--')}
            displayBenchmarkTooltip={displayBenchmarkTooltip}
            setDisplayBenchmarkTooltip={setDisplayBenchmarkTooltip}
          />
          <section className="tvt-main__content">
            <ShowInsights
              show={show}
              viewershipQueries={viewershipQueries}
              queryActions={queryActions}
              filtersActions={filtersActions}
              updateBenchmarkData={updateBenchmarkData}
              filters={filters}
              blocked={blocked}
              updateEpisodeOptionsLoader={updateEpisodeOptionsLoader}
            />
          </section>
        </>
      )}
    </Tab>
  );
};

export const renderEngagementTab = (
  {
    engagementQueries,
    fetchMemes,
    fetchComments,
    fetchMemesStatus,
    filters,
    filtersActions,
    show,
    queryActions,
    blocked,
    updateEpisodeOptionsLoader
  },
  currentTab,
  benchmarkData,
  updateBenchmarkData,
  benchmark,
  displayBenchmarkTooltip,
  setDisplayBenchmarkTooltip
) => {
  const fields = omit(filters.fields, 'show');

  const metricA = buildMetricObj(
    benchmarkData,
    'showReportEngagement',
    'completion',
    'Completion Rate',
    'Measures the percent of available content viewed among show watchers.',
    '0.0%'
  );
  const metricB = buildMetricObj(
    benchmarkData,
    'showReportEngagement',
    'engagementScore',
    'Engagement Score',
    'Measures an audience’s engagement to an episode, season or series. Engagement interactions are weighted depending on their importance normalized relative to the episodes tracked.',
    '0.00'
  );
  const metricC = buildMetricObj(
    benchmarkData,
    'showReportEngagement',
    'binge',
    'Binge Rate',
    'Measures the percent of viewers who watched 4 or more episodes in a 24 hour period.',
    '0.00%'
  );
  const metricD = buildMetricObj(
    benchmarkData,
    'showReportEngagement',
    'averageRating',
    'Audience Rating',
    'Measures the average audience rating of all episodes within that series on a scale from 1 to 5. This is used to indicate overall quality or satisfaction with a piece of content.',
    '0.0'
  );

  const benchmarkShowCount = get(
    benchmarkData,
    'showReportEngagementBottom.showCount',
    ''
  );

  return (
    <Tab
      className="tvt-main__content-tab"
      eventKey={showsConfig.SHOW_ENGAGEMENT_PAGE_ID}
      title="Engagement"
    >
      {currentTab === showsConfig.SHOW_ENGAGEMENT_PAGE_ID && (
        <>
          <BenchmarkBar
            loading={benchmarkData.loadingTop || benchmarkData.loadingBottom}
            appliedBenchmark={benchmark}
            applyBenchmarkFilters={updateBenchmarkData}
            filters={filters}
            titlesCount={numeral(benchmarkShowCount).format('0,0')}
            metricA={metricA}
            metricB={metricB}
            metricC={metricC}
            metricD={metricD}
            displayBenchmarkTooltip={displayBenchmarkTooltip}
            setDisplayBenchmarkTooltip={setDisplayBenchmarkTooltip}
          />
          <section className="tvt-main__content">
            <ShowEngagement
              engagementQueries={engagementQueries}
              updateBenchmarkData={updateBenchmarkData}
              show={show}
              episode={get(fields, 'episode.value.0', {})}
              season={get(fields, `season.value.0`, {})}
              fetchMemes={fetchMemes}
              fetchComments={fetchComments}
              fetchMemesStatus={fetchMemesStatus}
              filtersActions={filtersActions}
              filters={filters}
              queryActions={queryActions}
              updateEpisodeOptionsLoader={updateEpisodeOptionsLoader}
              blocked={blocked}
            />
          </section>
        </>
      )}
    </Tab>
  );
};

export const renderAffinityTab = (
  {
    affinityQueries,
    filters,
    filtersActions,
    show,
    queryActions,
    blocked,
    updateEpisodeOptionsLoader,
  },
  currentTab,
  hasShowAired
) => {
  return (
    <Tab
      className="tvt-main__content-tab"
      eventKey={showsConfig.SHOW_AFFINITY_PAGE_ID}
      title="Affinity"
    >
      {currentTab === showsConfig.SHOW_AFFINITY_PAGE_ID && (
        <>
          <section className="tvt-main__content">
            <ShowAffinity
              show={show}
              queryActions={queryActions}
              filtersActions={filtersActions}
              filters={filters}
              blocked={blocked}
              queries={affinityQueries}
              updateEpisodeOptionsLoader={updateEpisodeOptionsLoader}
              hasShowAired={hasShowAired}
            />
          </section>
        </>
      )}
    </Tab>
  );
}

renderAnticipationTab.propTypes = {
  anticipationLooks: PropTypes.shape({
    anticipationFollowerAffinity: PropTypes.shape({}),
    anticipationNewFollowers: PropTypes.shape({}),
    anticipationTotalFollowers: PropTypes.shape({})
  }).isRequired,

  anticipationQueries: PropTypes.shape({
    showFollowers: PropTypes.shape({})
  }).isRequired,

  show: PropTypes.shape({}).isRequired,
  filters: PropTypes.shape({}).isRequired
};

renderAudienceTab.propTypes = {
  viewershipLooks: PropTypes.shape({
    showAffinity: PropTypes.shape({}),
    showAgeIndex: PropTypes.shape({}),
    showCountryIndex: PropTypes.shape({}),
    showGenderIndex: PropTypes.shape({}),
    showViewerDropOff: PropTypes.shape({})
  }).isRequired,

  viewershipQueries: PropTypes.shape({
    showFavorites: PropTypes.shape({}),
    showFollowers: PropTypes.shape({}),
    showMobility: PropTypes.shape({}),
    showSampleSize: PropTypes.shape({}),
    showScoreBinge: PropTypes.shape({}),
    showScoreCompletion: PropTypes.shape({}),
    showScoreEngagement: PropTypes.shape({}),
    showScoreViewshare: PropTypes.shape({})
  }).isRequired,

  show: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired
  }).isRequired
};

renderEngagementTab.propTypes = {
  engagementQueries: PropTypes.shape({
    characterVoteEpisodeRank: PropTypes.shape({}),
    hottestMoments: PropTypes.shape({}),
    memesList: PropTypes.shape({}),
    showFollowers: PropTypes.shape({}),
    showEmotionIndex: PropTypes.shape({}),
    showEmotionVotes: PropTypes.shape({}),
    commentFeed: PropTypes.shape({})
  }).isRequired,

  engagementLooks: PropTypes.shape({
    engagementRawTotals: PropTypes.shape({}),
    showEpisodeEmotions: PropTypes.shape({})
  }).isRequired,
  fetchMemes: PropTypes.func.isRequired,
  fetchComments: PropTypes.func.isRequired,
  fetchMemesStatus: PropTypes.string.isRequired,

  show: PropTypes.shape({
    name: PropTypes.string.isRequired,
    id: PropTypes.string.isRequired
  }).isRequired,

  filters: PropTypes.shape({
    fields: FilterTypes.FilterSetType, // eslint-disable-line react/no-typos
    groupings: FilterTypes.GroupingSetType // eslint-disable-line react/no-typos
  }).isRequired,

  filtersActions: PropTypes.shape({
    getFiltersForPage: PropTypes.func.isRequired,
    getFiltersForPageAsync: PropTypes.func.isRequired,
    resetFilters: PropTypes.func.isRequired
  }).isRequired
};

export default {
  renderEngagementTab,
  renderAudienceTab,
  renderAnticipationTab,
  renderAffinityTab
};
